import { useCoreApiClient } from "@api/use-core-api-client";
import { IReactChildrenOnly } from "@custom-types/types";
import { TokensService } from "@services/integrations-service/tokens-service";
import { createContext, useContext, useMemo } from "react";

const TokensServiceContext = createContext<TokensService | undefined>(undefined);

/**
 * @returns an instance of the tokens service.
 * A context provider is needed to be able to use a single instance of
 * the tokens service in the application. Since the tokens service is
 * stateful and stores the state of ongoing integration authorizations
 * it's better to have a single instance.
 */
export function TokensServiceProvider({
  children,
}: IReactChildrenOnly): JSX.Element {
  const coreApiClient = useCoreApiClient();

  const tokensService = useMemo(() => {
    return new TokensService({ coreApiClient });
  }, [coreApiClient]);

  return (
    <TokensServiceContext.Provider value={tokensService}>
      {children}
    </TokensServiceContext.Provider>
  );
}

/**
 * Custom hook to consume the tokens service context.
 * @returns an instance of the tokens service.
 */
export function useTokensService(): TokensService {
  const context = useContext(TokensServiceContext);

  if (!context) {
    throw Error("useTokensService must be used within the TokensServiceProvider");
  }

  return context;
}
